// Lib
import { DependencyList, useCallback } from 'react';
import { createDispatchHook, createSelectorHook, Selector } from 'react-redux';
import Handsontable from 'handsontable';

// Context
import { HotTableStoreContext } from './HotTableStoreProvider';

// Actions
import { getActiveTableThunk } from './hotTableStoreActions';
import { HotTableStoreActions, HotTableStoreState, TableInstanceObject } from './hotTableStoreTypes';

export const useHotTableDispatch = createDispatchHook<HotTableStoreState, HotTableStoreActions>(HotTableStoreContext);
const hotTableSelectorHook = createSelectorHook<HotTableStoreState, HotTableStoreActions>(HotTableStoreContext);

export type ActiveHotTableSelector<ReturnedType = any, OwnProps = undefined> = Selector<
    Handsontable | undefined,
    ReturnedType,
    OwnProps
>;

/**
 * This hook allows consumers to select values from the active HoT table, without needing
 * to worry about finding the instance within the store's structure.
 *
 * The use of "activeTable.instance" is so that we can ensure a re-render on active table changes.
 * The activeTable is a stable reference, so we need to wrap it in an object to ensure the reference changes.
 *
 * This hook allows selectors to ignore this detail.
 */
export const useActiveTableSelector = <T>(selector: ActiveHotTableSelector<T>): T =>
    hotTableSelectorHook((state) => selector(state.activeTable?.instance));

/**
 * This hook allows the consumer to get the active HoT table instance directly,
 * however it is marked as "dangerous" because it could cause performance issues if it's used
 * in components that are regularly rendered.
 *
 * This is because the active table instance can change frequently, so if many components use
 * this, it could cause many unnecessary re-renders.
 */
export const dangerouslyUseActiveTable = () => hotTableSelectorHook((state) => state.activeTable)?.instance;

type ActiveTableCallback = (table?: Handsontable, ...args: any[]) => unknown;
type UseActiveTableCallback = (func: ActiveTableCallback, dependencies?: DependencyList) => (...args: any[]) => unknown;

/**
 * Fires a thunk to get the active HoT table from the HoT table store,
 * then provides the table to the callback function.
 */
export const useActiveTableCallback: UseActiveTableCallback = (func, dependencies) => {
    const dispatch = useHotTableDispatch();

    const returnFunc = (...args: unknown[]) => {
        // @ts-ignore HotTable thunk typings - FIXME
        const activeTable = dispatch(getActiveTableThunk()) as unknown as TableInstanceObject | null;
        return func(activeTable?.instance, ...args);
    };

    return dependencies ? useCallback(returnFunc, dependencies) : returnFunc;
};
